import { fetchData, setHeaderOptions } from "./apiHelpers";

// ---------------------------------------------------------------------------
// Endpoint base path — toggle for local vs non-prod later
// ---------------------------------------------------------------------------
const HARNESS_PROXY_BASE = "/admin/dev-tools/medication-order-harness";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Legacy run-all-in-one input (kept for backwards compat) */
export type RunMedicationOrderHarnessInput = {
  medication_name_original: string;
  seed_resolved: boolean;
  run_twice: boolean;
};

export type RunMedicationOrderHarnessResponse = {
  ok: boolean;
  diagnostics?: Record<string, unknown>;
  error?: string;
};

/** v1 create-order payload — uses user_id only, never patient_id */
export type CreateHarnessOrderInput = {
  user_id: string;
  medication_name_original: string;
  seed_resolved?: boolean;
};

export type CreateHarnessOrderResponse = {
  order_id: string;
  status: string;
  [key: string]: unknown;
};

export type HarnessTransitionStep = {
  step:
    | "submit_for_review"
    | "approve"
    | "reject"
    | "queue_for_dispatch"
    | "cancel";
  reason?: string | null;
};

export type RunHarnessTransitionsInput = {
  steps: HarnessTransitionStep[];
  stop_on_error: boolean;
};

export type HarnessTransitionResult = {
  step: string;
  status: "success" | "failed" | "skipped";
  error?: string | null;
  details?: Record<string, unknown>;
};

export type RunHarnessTransitionsResponse = {
  order_id: string;
  results: HarnessTransitionResult[];
  final_status: string;
  [key: string]: unknown;
};

export type HarnessDiagnosticsResponse = {
  order_id: string;
  events?: unknown[];
  timeline?: unknown[];
  correlation_ids?: string[];
  idempotency_keys?: string[];
  last_webhook_data?: Record<string, unknown> | null;
  [key: string]: unknown;
};

// Legacy fixture type (kept for backwards compat with old hook)
export type CreateMedicationOrderHarnessFixtureInput = {
  medication_name_original?: string;
  seed_resolved?: boolean;
  patient_id?: string;
  destination_fax?: string | null;
  provider_id?: string | null;
  pharmacy_id?: string | null;
};

export type MedicationOrderHarnessTransitionStep = HarnessTransitionStep;

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/** Legacy run (all-in-one) */
export async function runMedicationOrderHarness(
  input: RunMedicationOrderHarnessInput
): Promise<RunMedicationOrderHarnessResponse> {
  return fetchData<RunMedicationOrderHarnessResponse>(
    `${HARNESS_PROXY_BASE}/run`,
    setHeaderOptions({ method: "POST", body: input })
  );
}

/** v1: Create a harness fixture order */
export async function createHarnessOrder(
  input: CreateHarnessOrderInput
): Promise<CreateHarnessOrderResponse> {
  return fetchData<CreateHarnessOrderResponse>(
    `${HARNESS_PROXY_BASE}/orders`,
    setHeaderOptions({ method: "POST", body: input })
  );
}

/** v1: Run sequential transitions against an order */
export async function runHarnessTransitions(
  orderId: string,
  input: RunHarnessTransitionsInput
): Promise<RunHarnessTransitionsResponse> {
  return fetchData<RunHarnessTransitionsResponse>(
    `${HARNESS_PROXY_BASE}/orders/${orderId}/transitions`,
    setHeaderOptions({ method: "POST", body: input })
  );
}

/** v1: Fetch diagnostics for an order */
export async function getHarnessDiagnostics(
  orderId: string
): Promise<HarnessDiagnosticsResponse> {
  return fetchData<HarnessDiagnosticsResponse>(
    `${HARNESS_PROXY_BASE}/orders/${orderId}/diagnostics`,
    setHeaderOptions({ method: "GET" })
  );
}

// Legacy wrappers (used by existing hook)
export async function createMedicationOrderHarnessFixture(
  input: CreateMedicationOrderHarnessFixtureInput
): Promise<any> {
  return fetchData<any>(
    `${HARNESS_PROXY_BASE}/orders`,
    setHeaderOptions({ method: "POST", body: input })
  );
}

export async function runMedicationOrderHarnessTransitions(
  orderId: string,
  steps: MedicationOrderHarnessTransitionStep[],
  stopOnError = true
): Promise<any> {
  return fetchData<any>(
    `${HARNESS_PROXY_BASE}/orders/${orderId}/transitions`,
    setHeaderOptions({
      method: "POST",
      body: { steps, stop_on_error: stopOnError },
    })
  );
}

export async function getMedicationOrderHarnessDiagnostics(
  orderId: string
): Promise<any> {
  return fetchData<any>(
    `${HARNESS_PROXY_BASE}/orders/${orderId}/diagnostics`,
    setHeaderOptions({ method: "GET" })
  );
}

export type HarnessQueueStatusResponse = {
  configured: boolean;
  messages_visible: number;
  messages_in_flight: number;
  dlq_messages_visible: number;
  checked_at: string;
};

export async function getHarnessQueueStatus(): Promise<HarnessQueueStatusResponse> {
  return fetchData<HarnessQueueStatusResponse>(
    `${HARNESS_PROXY_BASE}/queue-status`,
    setHeaderOptions({ method: "GET" })
  );
}
